import jsPDF from 'jspdf';
import { format } from 'date-fns';
import { salesTaxRatesAPI } from '@shared/apiClient';
import { computeInvoiceTotals, DEFAULT_CATEGORY_RULES } from '@shared/salesTaxUtils';

export const buildInvoiceDataFromServiceCall = (serviceCall) => {
  const workOrder = serviceCall.cspireWorkOrder || {};
  const site = workOrder.site || {};
  const schedule = workOrder.schedule || {};
  const visits = serviceCall.visits || [];
  // Walk visits newest-first to find the most recent one that actually
  // recorded a generator hours reading; falls back to the service call.
  const lastHoursVisit = [...visits].reverse().find((v) => v && v.generatorHours != null);

  const invoiceNumber = serviceCall.invoiceNumber || 'Pending';
  const invoiceDate = serviceCall.completedDate
    ? format(new Date(serviceCall.completedDate), 'M/d/yyyy')
    : format(new Date(), 'M/d/yyyy');
  const serviceDate = serviceCall.completedDate
    ? format(new Date(serviceCall.completedDate), 'MM-dd-yyyy')
    : format(new Date(), 'MM-dd-yyyy');

  const billTo = [
    'C Spire Wireless',
    'Technical Dept',
    '1018 Highland Colony Pkwy, Suite 390',
    'Ridgeland, MS 39157'
  ];

  const locationName = workOrder.locationName || site.locationName || '';
  const siteId = site.siteId || '';
  const siteAddress = site.addressLine1 || '';
  const siteCityStateZip = [site.city, site.state, site.zipCode].filter(Boolean).join(' ');
  const openedBy = workOrder.openedByName || '';

  const workOrderNumber = workOrder.workOrderNumber || '';
  const inv = serviceCall.invoiceData || {};
  const pickInv = (key, fallback) =>
    (Object.prototype.hasOwnProperty.call(inv, key) ? inv[key] ?? '' : fallback ?? '');

  const generatorModel = pickInv(
    'generatorModel',
    serviceCall.generatorModel || site.generatorModel || serviceCall?.client?.generatorModel || ''
  );
  const generatorSerialNumber = pickInv(
    'generatorSerialNumber',
    serviceCall.generatorSerialNumber || site.generatorSerialNumber || serviceCall?.client?.generatorSerialNumber || ''
  );
  const airFilter = pickInv('airFilter', site.airFilter || '');
  const fuelFilter1 = pickInv('fuelFilter1', site.fuelFilter1 || '');
  const fuelFilter2 = pickInv('fuelFilter2', site.fuelFilter2 || '');
  const oilFilter1 = pickInv('oilFilter1', site.oilFilter1 || '');
  const oilFilter2 = pickInv('oilFilter2', site.oilFilter2 || '');

  const hoursRun = lastHoursVisit?.generatorHours ?? serviceCall.generatorHours ?? '';
  const lastFuelLevelVisit = [...visits].reverse().find((v) => v && v.fuelLevel != null && String(v.fuelLevel).trim() !== '');
  const fuelLevel = lastFuelLevelVisit?.fuelLevel || '';
  const serviceType = serviceCall.type?.name || 'Service';
  // Combine work performed across every visit so multi-trip jobs don't lose
  // earlier visits' notes on the invoice.
  const visitWorkEntries = visits
    .map((v, idx) => ({
      number: v.visitNumber || idx + 1,
      text: typeof v.workPerformed === 'string' ? v.workPerformed.trim() : ''
    }))
    .filter((entry) => entry.text);
  const aggregatedWorkPerformed = visitWorkEntries.length > 1
    ? visitWorkEntries.map((entry) => `Visit ${entry.number}: ${entry.text}`).join('\n\n')
    : (visitWorkEntries[0]?.text || '');
  const workPerformed = aggregatedWorkPerformed || serviceCall.workPerformed || '';

  const lineItems = [];
  const scheduleAmount = Number(schedule.amount || 0);
  const scheduleName = schedule.name || schedule.description || '';
  if (scheduleName && (scheduleName !== 'Scheduled' || scheduleAmount > 0)) {
    const scheduleCode = schedule.code || 'prod0000000';
    const scheduleCodeLabel = scheduleName.split(' ')[0] || '';
    lineItems.push({
      itemNumber: scheduleCode,
      code: scheduleCodeLabel,
      description: scheduleName,
      unitPrice: scheduleAmount,
      quantity: 1,
      category: 'labor'
    });
  }

  const parts = [];
  if (visits.length > 0) {
    visits.forEach((visit) => {
      if (Array.isArray(visit.partsUsed)) {
        parts.push(...visit.partsUsed);
      }
    });
  }
  // Also include root-level parts (e.g. parts added by admin via edit modal)
  if (Array.isArray(serviceCall.partsUsed)) {
    const visitPartKeys = new Set(parts.map((p) => `${p.itemNumber || p.name}|${p.quantity}|${p.cost}`));
    serviceCall.partsUsed.forEach((p) => {
      const key = `${p.itemNumber || p.name}|${p.quantity}|${p.cost}`;
      if (!visitPartKeys.has(key)) {
        parts.push(p);
      }
    });
  }

  parts.forEach((part) => {
    const quantity = parseFloat(part.quantity) || 1;
    const unitPrice = parseFloat(part.cost ?? part.price ?? part.unitPrice ?? 0);
    lineItems.push({
      itemNumber: part.itemNumber || part.name || 'part',
      code: part.salesTaxCode || '',
      description: part.description || part.name || part.itemNumber || 'Part',
      unitPrice,
      quantity,
      category: 'parts'
    });
  });

  // Include tech draft lines from invoiceData (custom billing without schedule)
  if (!scheduleName && inv?.lineItems) {
    inv.lineItems.forEach((item) => {
      lineItems.push({
        itemNumber: item.itemNumber || '',
        code: item.code || '',
        description: item.description || '',
        unitPrice: item.unitPrice != null ? Number(item.unitPrice) : 0,
        quantity: parseFloat(item.quantity) || 1,
        category: item.category || 'other'
      });
    });
  }

  return {
    invoiceNumber,
    invoiceDate,
    billTo,
    siteLocation: { locationName, siteId, address: siteAddress, cityStateZip: siteCityStateZip, openedBy },
    workOrderNumber,
    generatorModel,
    generatorSerialNumber,
    airFilter,
    fuelFilter1,
    fuelFilter2,
    oilFilter1,
    oilFilter2,
    serviceDate,
    hoursRun: hoursRun ? String(parseFloat(hoursRun).toFixed(0)) : '',
    fuelLevel,
    serviceType,
    workPerformed,
    lineItems,
    taxRate: inv.taxRate != null ? Number(inv.taxRate) : 0.07,
    taxEnabled: inv.taxEnabled !== false,
    taxCategoryRules: inv.taxCategoryRules || DEFAULT_CATEGORY_RULES
  };
};

export const enrichInvoiceDataWithTax = async (serviceCall, data) => {
  const site = serviceCall.cspireWorkOrder?.site;
  if (site?.taxExempt || serviceCall.client?.taxExempt) {
    return {
      ...data,
      taxEnabled: false,
      taxCategoryRules: data.taxCategoryRules || DEFAULT_CATEGORY_RULES
    };
  }

  try {
    const params = {};
    if (serviceCall.client?.id) params.clientId = serviceCall.client.id;
    else if (site?.state) params.state = site.state;
    else if (serviceCall.client?.state) params.state = serviceCall.client.state;

    if (Object.keys(params).length > 0) {
      const res = await salesTaxRatesAPI.resolveTaxContext(params);
      const ctx = res.data.taxContext;
      return {
        ...data,
        taxEnabled: ctx.taxEnabled,
        taxRate: site?.defaultTaxRate != null ? Number(site.defaultTaxRate) : ctx.taxRate,
        taxCategoryRules: ctx.taxCategoryRules || DEFAULT_CATEGORY_RULES
      };
    }
  } catch (error) {
    console.warn('Failed to resolve tax context for PDF:', error);
  }

  return data;
};

// Layer a saved invoiceData blob on top of freshly-built defaults so partial
// drafts (e.g. tech custom-billing saves with only `lineItems`) don't blank
// out every other field on the invoice.
export const mergeInvoiceData = (built, saved) => {
  if (!saved || typeof saved !== 'object') return built;
  const merged = { ...built };
  Object.keys(saved).forEach((key) => {
    const value = saved[key];
    if (value === undefined || value === null || value === '') return;
    merged[key] = value;
  });
  merged.billTo = Array.isArray(saved.billTo) && saved.billTo.length
    ? saved.billTo
    : built.billTo;
  merged.siteLocation = {
    ...(built.siteLocation || {}),
    ...(saved.siteLocation || {})
  };
  merged.lineItems = Array.isArray(saved.lineItems) && saved.lineItems.length
    ? saved.lineItems
    : built.lineItems;
  merged.taxRate = saved.taxRate != null ? Number(saved.taxRate) : built.taxRate;
  if (saved.taxEnabled !== undefined) merged.taxEnabled = saved.taxEnabled !== false;
  if (saved.taxCategoryRules) merged.taxCategoryRules = saved.taxCategoryRules;
  return merged;
};

export const resolveInvoiceData = (serviceCall, override = null) => {
  if (override) return override;
  return mergeInvoiceData(
    buildInvoiceDataFromServiceCall(serviceCall),
    serviceCall.invoiceData
  );
};

const buildCspireWorkOrderPDF = (serviceCall, invoiceDataOverride = null) => {
  const data = resolveInvoiceData(serviceCall, invoiceDataOverride);

  const doc = new jsPDF('p', 'pt', 'letter');
  const pageWidth = doc.internal.pageSize.getWidth();

  const formatMoney = (value) => new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(Number(value || 0));

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.text('K & R Services, Inc', 55, 52);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.text('P.O.Box 216', 92, 68);
  doc.text('1911 Kenneth Avenue,', 55, 84);
  doc.text('Pascagoula, MS. 39567', 55, 100);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.text('INVOICE #:', 326, 52);
  doc.setFont('helvetica', 'normal');
  doc.text(data.invoiceNumber || 'Pending', 425, 52);
  doc.text(data.invoiceDate || '', 331, 78);

  const billToLines = data.billTo || [];
  const site = data.siteLocation || {};
  const siteLines = [
    site.locationName,
    site.siteId ? `Site # ${site.siteId}` : '',
    site.address,
    site.cityStateZip,
    site.openedBy
  ].filter(Boolean);

  const boxTopY = 118;
  const boxPaddingX = 10;
  const boxPaddingY = 10;
  const lineHeight = 13;
  const leftBoxX = 45;
  const rightBoxX = 330;
  const leftBoxWidth = 250;
  const rightBoxWidth = 230;
  const maxLines = Math.max(billToLines.length, siteLines.length, 1);
  const boxHeight = (maxLines * lineHeight) + (boxPaddingY * 2);
  const leftTextX = leftBoxX + boxPaddingX;
  const rightTextX = rightBoxX + boxPaddingX;
  const textStartY = boxTopY + boxPaddingY + 2;

  doc.rect(leftBoxX, boxTopY, leftBoxWidth, boxHeight);
  doc.rect(rightBoxX, boxTopY, rightBoxWidth, boxHeight);

  let billToY = textStartY;
  billToLines.forEach((line) => {
    doc.text(line, leftTextX, billToY);
    billToY += lineHeight;
  });

  let siteY = textStartY;
  siteLines.forEach((line) => {
    doc.text(line, rightTextX, siteY);
    siteY += lineHeight;
  });

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.text('Work Order', 55, 227);
  doc.setFont('helvetica', 'normal');
  doc.text(data.workOrderNumber || '', 122, 227);

  if (data.generatorModel || data.generatorSerialNumber) {
    doc.text('Gen_Mod', 294, 225);
    if (data.generatorModel) {
      doc.text(data.generatorModel, 347, 225);
    }
    if (data.generatorSerialNumber) {
      doc.text('Gen S/N', 438, 225);
      doc.text(data.generatorSerialNumber, 490, 225);
    }
  }

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.text('Date of Svc', 171, 268);
  doc.text('Hours Run', 282, 268);
  doc.text('Fuel Level', 394, 270);
  doc.text('Type Service', 468, 270);

  doc.setFont('helvetica', 'normal');
  doc.text(data.serviceDate || '', 170, 290);

  if (data.hoursRun) {
    doc.text(String(data.hoursRun), 278, 290);
  }

  if (data.fuelLevel) {
    doc.text(String(data.fuelLevel), 390, 290);
  }

  doc.text(data.serviceType || 'Service', 470, 290);

  const pageHeight = doc.internal.pageSize.getHeight();
  const filterParts = [];
  if (data.airFilter) filterParts.push(`Air: ${data.airFilter}`);
  if (data.fuelFilter1) filterParts.push(`Fuel 1: ${data.fuelFilter1}`);
  if (data.fuelFilter2) filterParts.push(`Fuel 2: ${data.fuelFilter2}`);
  if (data.oilFilter1) filterParts.push(`Oil 1: ${data.oilFilter1}`);
  if (data.oilFilter2) filterParts.push(`Oil 2: ${data.oilFilter2}`);
  const filterBlockText = filterParts.join('  |  ');

  let workY = 320;
  if (filterBlockText) {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.text('Filters:', 50, workY);
    doc.setFont('helvetica', 'normal');
    const filterLines = doc.splitTextToSize(filterBlockText, 505);
    let fy = workY + 12;
    filterLines.forEach((line) => {
      doc.text(line, 50, fy);
      fy += 11;
    });
    workY = fy + 10;
  }
  if (data.workPerformed) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(12);
    const workLines = doc.splitTextToSize(data.workPerformed, 505);
    doc.text(workLines, 50, workY);
    workY += workLines.length * 17;
  }

  const lineItems = (data.lineItems || []).map((item) => ({
    ...item,
    category: item.category || 'other',
    amount: (parseFloat(item.unitPrice) || 0) * (parseFloat(item.quantity) || 1)
  }));

  const lineItemHeight = 23;
  const bottomSafe = pageHeight - 120;
  let lineItemY = Math.max(380, workY + 20);

  const ensureLineSpace = () => {
    if (lineItemY + lineItemHeight > bottomSafe) {
      doc.addPage();
      lineItemY = 55;
      doc.setFont('helvetica', 'italic');
      doc.setFontSize(9);
      doc.text('Line items (continued)', 55, lineItemY);
      lineItemY += 18;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
    }
  };

  if (lineItemY > bottomSafe - 40) {
    doc.addPage();
    lineItemY = 55;
  }

  lineItems.forEach((item) => {
    ensureLineSpace();
    doc.text(String(item.itemNumber || ''), 59, lineItemY);
    if (item.code) {
      doc.text(String(item.code), 167, lineItemY + 0.2);
    }
    doc.text(String(item.description || ''), 254, lineItemY);
    doc.text(formatMoney(item.unitPrice), 380, lineItemY);
    doc.text(String(item.quantity), 437, lineItemY);
    doc.text(formatMoney(item.amount), 493, lineItemY);
    lineItemY += lineItemHeight;
  });

  const taxEnabled = data.taxEnabled !== false;
  const taxRate = data.taxRate != null ? Number(data.taxRate) : 0.07;
  const taxCategoryRules = data.taxCategoryRules || DEFAULT_CATEGORY_RULES;
  const totals = computeInvoiceTotals(lineItems, { taxEnabled, taxRate, taxCategoryRules });
  const { subtotal, taxAmount, total: grandTotal } = totals;

  let totalsY = lineItemY + 28;
  if (totalsY + 88 > pageHeight - 40) {
    doc.addPage();
    totalsY = 55;
  }

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.text('Total:', 453, totalsY);
  doc.text(formatMoney(subtotal), 508, totalsY);
  if (taxEnabled) {
    doc.text('Tax Rate', 159, totalsY + 13);
    doc.text(`${(taxRate * 100).toFixed(1)}%`, 227, totalsY + 13);
    doc.text('Tax:', 458, totalsY + 13);
    doc.text(formatMoney(taxAmount), 514, totalsY + 13);
  } else {
    doc.text('Tax: Exempt', 159, totalsY + 13);
  }
  doc.text('Grand Total:', 443, totalsY + 26);
  doc.text(formatMoney(grandTotal), 508, totalsY + 26);

  const footerY = pageHeight - 36;
  const footerText = 'Tel: 228 762 8535,  Email: office@kandrservices.com';
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  const footerWidth = doc.getTextWidth(footerText);
  doc.text(footerText, (pageWidth - footerWidth) / 2, footerY);

  const clientName = `cspire_${data.workOrderNumber || 'work-order'}`.replace(/[^a-z0-9]/gi, '_').substring(0, 30);
  return { doc, serviceDate: data.serviceDate, clientName };
};

// Helper function to build the PDF document
const buildServiceCallPDF = (serviceCall) => {
  const doc = new jsPDF('p', 'mm', 'letter');
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 15;
  const contentWidth = pageWidth - (margin * 2);
  let yPos = margin;

  const client = serviceCall.client;
  const franklinSite = serviceCall.franklinSite;
  const isFranklinSite = !!serviceCall.isFranklin && franklinSite;
  const technician = serviceCall.technician;

  // Company Header (Left Side)
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text('K & R Services, Inc', margin, yPos);
  yPos += 5;
  
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text('P.O.Box 216', margin, yPos);
  yPos += 4;
  doc.text('1911 Kenneth Avenue,', margin, yPos);
  yPos += 4;
  doc.text('Pascagoula, MS  39567', margin, yPos);
  
  // Invoice # and Date (Right Side)
  const rightX = pageWidth - margin;
  let rightYPos = margin;
  
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  const invoiceLabel = 'Invoice #:';
  doc.text(invoiceLabel, rightX - doc.getTextWidth(invoiceLabel), rightYPos);
  rightYPos += 6;
  
  // Invoice Number
  if (serviceCall.invoiceNumber) {
    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    const invoiceNum = serviceCall.invoiceNumber;
    doc.text(invoiceNum, rightX - doc.getTextWidth(invoiceNum), rightYPos);
    rightYPos += 8;
  } else {
    rightYPos += 2;
  }
  
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  const dateStr = serviceCall.completedDate 
    ? format(new Date(serviceCall.completedDate), 'MM/dd/yyyy')
    : format(new Date(), 'MM/dd/yyyy');
  const dateLabel = `Date:    ${dateStr}`;
  doc.text(dateLabel, rightX - doc.getTextWidth(dateLabel), rightYPos);
  
  yPos += 8;
  
  // Customer Information
  yPos += 8;
  const customerInfoStartY = yPos;
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  
  const customerName = isFranklinSite
    ? `Franklin — ${franklinSite.locationName || franklinSite.siteId || 'Site'}`
    : (client?.isCommercial && client?.companyName
        ? client.companyName
        : `${client?.contactFirstName || ''} ${client?.contactLastName || ''}`.trim() || 'N/A');
  doc.text(customerName, margin, yPos);
  yPos += 4;

  if (isFranklinSite) {
    if (franklinSite.company) {
      doc.text(`Company: ${franklinSite.company}`, margin, yPos);
      yPos += 4;
    }
    if (franklinSite.siteId) {
      doc.text(`Site ID: ${franklinSite.siteId}`, margin, yPos);
      yPos += 4;
    }
  }

  const streetAddress = isFranklinSite ? franklinSite.address : client?.address;
  if (streetAddress) {
    doc.text(streetAddress, margin, yPos);
    yPos += 4;
  }

  const cityStateZip = isFranklinSite
    ? [franklinSite.city, franklinSite.state, franklinSite.zipCode].filter(Boolean).join(', ')
    : `${client?.city || ''}${client?.city && client?.state ? ', ' : ''}${client?.state || ''}`;
  if (cityStateZip.trim()) {
    doc.text(cityStateZip, margin, yPos);
    yPos += 4;
  }
  
  yPos += 6;
  
  // Work Order Section
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text('Work Order', margin, yPos);
  
  // Generator Details - Right side, larger font, no box
  const genInfoX = pageWidth - margin - 70; // Right side position
  let genYPos = customerInfoStartY;

  const genModel = isFranklinSite ? franklinSite.generatorModel : client?.generatorModel;
  const genSerial = isFranklinSite ? franklinSite.generatorSerialNumber : client?.generatorSerialNumber;

  if (genModel || genSerial) {
    doc.setFontSize(12); // Much larger font
    
    if (genModel) {
      doc.setFont('helvetica', 'bold');
      doc.text('Model:', genInfoX, genYPos);
      doc.setFont('helvetica', 'normal');
      doc.text(String(genModel), genInfoX + 18, genYPos);
      genYPos += 6;
    }
    
    if (genSerial) {
      doc.setFont('helvetica', 'bold');
      doc.text('Serial #:', genInfoX, genYPos);
      doc.setFont('helvetica', 'normal');
      doc.text(String(genSerial), genInfoX + 22, genYPos);
      genYPos += 6;
    }
    
    // Update yPos if generator info extends past current position
    if (genYPos > yPos + 8) {
      yPos = genYPos;
    } else {
      yPos += 8;
    }
  } else {
    yPos += 8;
  }
  
  // Service Details Table
  // Draw horizontal line
  doc.setDrawColor(0);
  doc.setLineWidth(0.5);
  doc.line(margin, yPos, pageWidth - margin, yPos);
  yPos += 2;
  
  // Table Headers
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  const tableHeaderY = yPos + 5;
  
  const colXPositions = {
    dateOfSvc: margin + 5,
    hoursRun: margin + 40,
    typeService: margin + 75
  };
  
  doc.text('Date of Svc', colXPositions.dateOfSvc, tableHeaderY);
  doc.text('Hours Run', colXPositions.hoursRun, tableHeaderY);
  doc.text('Type Service', colXPositions.typeService, tableHeaderY);
  
  yPos += 8;
  
  // Draw horizontal line under headers
  doc.line(margin, yPos, pageWidth - margin, yPos);
  yPos += 2;
  
  // Table Data
  doc.setFont('helvetica', 'normal');
  const dataY = yPos + 5;
  
  const serviceDate = serviceCall.completedDate 
    ? format(new Date(serviceCall.completedDate), 'MM-dd-yyyy')
    : format(new Date(), 'MM-dd-yyyy');
  doc.text(serviceDate, colXPositions.dateOfSvc, dataY);
  
  // Generate client name for filename
  const clientNameForFile = isFranklinSite
    ? (franklinSite.locationName || franklinSite.siteId || 'Franklin_site')
    : (client?.isCommercial && client?.companyName
        ? client.companyName
        : `${client?.contactFirstName || ''}_${client?.contactLastName || ''}`.trim() || 'service-call');
  const clientName = clientNameForFile.replace(/[^a-z0-9]/gi, '_').substring(0, 30);
  
  // Get generator hours from the most recent visit, fallback to service call's generatorHours
  let latestGeneratorHours = null;
  if (serviceCall.visits && serviceCall.visits.length > 0) {
    // Find the most recent visit (highest visitNumber or most recent visitDate)
    const sortedVisits = [...serviceCall.visits].sort((a, b) => {
      // Sort by visitDate if available, otherwise by visitNumber
      if (a.visitDate && b.visitDate) {
        return new Date(b.visitDate) - new Date(a.visitDate);
      }
      return (b.visitNumber || 0) - (a.visitNumber || 0);
    });
    latestGeneratorHours = sortedVisits[0]?.generatorHours;
  }
  
  // Fallback to service call's generatorHours if no visits have it
  const hoursRun = latestGeneratorHours 
    ? parseFloat(latestGeneratorHours).toFixed(0)
    : serviceCall.generatorHours 
      ? parseFloat(serviceCall.generatorHours).toFixed(0)
      : '';
  if (hoursRun) {
    doc.text(hoursRun, colXPositions.hoursRun, dataY);
  }
  
  const serviceType = serviceCall.type?.name || 'Service Call';
  doc.text(serviceType, colXPositions.typeService, dataY);
  
  yPos += 10;
  
  // Visits Section - Show all visits with their details
  if (serviceCall.visits && serviceCall.visits.length > 0) {
    // Sort visits by visit number
    const sortedVisits = [...serviceCall.visits].sort((a, b) => a.visitNumber - b.visitNumber);
    
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    const visitsTitle = sortedVisits.length > 1 
      ? `Service Visits (${sortedVisits.length} total):`
      : 'Service Visit:';
    doc.text(visitsTitle, margin, yPos);
    yPos += 6;
    
    sortedVisits.forEach((visit, index) => {
      // Check if we need a new page
      if (yPos > pageHeight - 40) {
        doc.addPage();
        yPos = margin;
      }
      
      // Visit Header
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      if (sortedVisits.length > 1) {
        doc.text(`Visit ${visit.visitNumber}:`, margin + 5, yPos);
      }
      yPos += 4;
      
      // Visit Date
      if (visit.visitDate) {
        doc.setFont('helvetica', 'normal');
        const visitDateStr = format(new Date(visit.visitDate), 'MM/dd/yyyy');
        doc.text(`Date: ${visitDateStr}`, margin + 10, yPos);
        yPos += 4;
      }
      
      // Technician
      if (visit.technician) {
        doc.text(`Technician: ${visit.technician.firstName} ${visit.technician.lastName}`, margin + 10, yPos);
        yPos += 4;
      }
      
      // Service Type for this visit
      if (visit.type) {
        doc.text(`Service Type: ${visit.type.name}`, margin + 10, yPos);
        yPos += 4;
      }
      
      // Generator Hours, Labor Hours, Mileage
      if (visit.generatorHours || visit.laborHours || visit.mileage) {
        let metricsText = '';
        if (visit.generatorHours) {
          metricsText += `Generator Hours: ${parseFloat(visit.generatorHours).toFixed(1)}`;
        }
        if (visit.laborHours) {
          if (metricsText) metricsText += '  |  ';
          metricsText += `Labor Hours: ${parseFloat(visit.laborHours).toFixed(2)}`;
        }
        if (visit.mileage) {
          if (metricsText) metricsText += '  |  ';
          metricsText += `Mileage: ${parseFloat(visit.mileage).toFixed(1)}`;
        }
        doc.text(metricsText, margin + 10, yPos);
        yPos += 4;
      }
      
      // Work Performed for this visit
      if (visit.workPerformed) {
        doc.setFont('helvetica', 'bold');
        doc.text('Work Performed:', margin + 10, yPos);
        yPos += 4;
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(12);
        const workLines = doc.splitTextToSize(visit.workPerformed, contentWidth - 20);
        doc.text(workLines, margin + 15, yPos);
        yPos += workLines.length * 5 + 2;
        doc.setFontSize(9);
      }
      
      // Outcome
      if (visit.outcome) {
        doc.setFont('helvetica', 'bold');
        const outcomeText = visit.outcome === 'completed' ? 'Status: Completed' : 'Status: Needs Return';
        doc.text(outcomeText, margin + 10, yPos);
        yPos += 4;
        doc.setFont('helvetica', 'normal');
        
        if (visit.outcome === 'needs_return' && visit.reasonForReturn) {
          doc.text('Reason for Return:', margin + 10, yPos);
          yPos += 4;
          const reasonLines = doc.splitTextToSize(visit.reasonForReturn, contentWidth - 20);
          doc.text(reasonLines, margin + 15, yPos);
          yPos += reasonLines.length * 4;
        }
      }
      
      yPos += 6; // Space between visits
    });
    
    yPos += 4;
  } else {
    // Fallback to old format if no visits data
    yPos += 6;
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    
    if (serviceCall.workPerformed) {
      doc.setFontSize(12);
      const workLines = doc.splitTextToSize(serviceCall.workPerformed, contentWidth - 10);
      doc.text(workLines, margin + 5, yPos);
      yPos += workLines.length * 5 + 4;
      doc.setFontSize(9);
    }
    
    yPos += 10;
  }
  
  // Line Items Summary Table
  yPos += 4;
  doc.setDrawColor(0);
  doc.setLineWidth(0.5);
  doc.line(margin, yPos, pageWidth - margin, yPos);
  yPos += 8;
  
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text('Summary of Labor and Parts:', margin, yPos);
  yPos += 6;
  
  doc.setFont('helvetica', 'normal');
  
  const itemCol = margin + 15;
  const descCol = margin + 55;
  const qtyCol = pageWidth - margin - 50;
  
  // Calculate totals from visits
  let totalLaborHours = 0;
  let totalMileage = 0;
  const allParts = [];
  
  if (serviceCall.visits && serviceCall.visits.length > 0) {
    serviceCall.visits.forEach((visit) => {
      totalLaborHours += parseFloat(visit.laborHours) || 0;
      totalMileage += parseFloat(visit.mileage) || 0;
      if (visit.partsUsed && visit.partsUsed.length > 0) {
        visit.partsUsed.forEach((part) => {
          allParts.push(part);
        });
      }
    });
    // Also include root-level parts not already covered by visits
    if (serviceCall.partsUsed && serviceCall.partsUsed.length > 0) {
      const visitPartKeys = new Set(allParts.map((p) => `${p.itemNumber || p.name}|${p.quantity}|${p.cost}`));
      serviceCall.partsUsed.forEach((p) => {
        const key = `${p.itemNumber || p.name}|${p.quantity}|${p.cost}`;
        if (!visitPartKeys.has(key)) {
          allParts.push(p);
        }
      });
    }
  } else {
    totalLaborHours = parseFloat(serviceCall.laborHours) || 0;
    totalMileage = parseFloat(serviceCall.mileage) || 0;
    if (serviceCall.partsUsed && serviceCall.partsUsed.length > 0) {
      serviceCall.partsUsed.forEach((part) => {
        allParts.push(part);
      });
    }
  }
  
  // Labor row
  if (totalLaborHours > 0) {
    doc.text('prod0000486', itemCol, yPos);
    doc.text('Labor', descCol, yPos);
    doc.text('labor', descCol + 30, yPos);
    doc.text(totalLaborHours.toFixed(2), qtyCol, yPos);
    yPos += 6;
  }
  
  // Mileage row
  if (totalMileage > 0) {
    doc.text('prod0000486', itemCol, yPos);
    doc.text('Mileage', descCol, yPos);
    doc.text('mileage', descCol + 30, yPos);
    doc.text(totalMileage.toFixed(2), qtyCol, yPos);
    yPos += 6;
  }
  
  // All Parts Used
  if (allParts.length > 0) {
    allParts.forEach((part) => {
      // Check if we need a new page
      if (yPos > pageHeight - 30) {
        doc.addPage();
        yPos = margin;
      }
      
      const itemNumber = part.itemNumber || 'N/A';
      const description = part.description || part.name || '';
      const quantity = parseFloat(part.quantity) || 0;
      
      doc.text(itemNumber, itemCol, yPos);
      
      // Truncate description if too long
      const truncDesc = description.substring(0, 40);
      doc.text(truncDesc, descCol, yPos);
      doc.text(quantity.toFixed(2), qtyCol, yPos);
      yPos += 6;
    });
  }
  
  // Footer
  const footerY = pageHeight - 20;
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  
  const footerText = 'Tel: 228 762 8535,  Email: office@kandrservices.com';
  const footerWidth = doc.getTextWidth(footerText);
  doc.text(footerText, (pageWidth - footerWidth) / 2, footerY);

  return { doc, serviceDate, clientName };
};

export const generateServiceCallPDF = async (serviceCall, invoiceData = null) => {
  let data = resolveInvoiceData(serviceCall, invoiceData);
  if (serviceCall.isCspire) {
    data = await enrichInvoiceDataWithTax(serviceCall, data);
  }
  const { doc, serviceDate, clientName } = serviceCall.isCspire
    ? buildCspireWorkOrderPDF(serviceCall, data)
    : buildServiceCallPDF(serviceCall);

  const filename = `${(serviceDate || '').replace(/-/g, '')}_${clientName}_invoice.pdf`;
  doc.save(filename);
};

export const printServiceCallPDF = async (serviceCall, invoiceData = null) => {
  let data = resolveInvoiceData(serviceCall, invoiceData);
  if (serviceCall.isCspire) {
    data = await enrichInvoiceDataWithTax(serviceCall, data);
  }
  const { doc } = serviceCall.isCspire
    ? buildCspireWorkOrderPDF(serviceCall, data)
    : buildServiceCallPDF(serviceCall);

  doc.autoPrint();
  doc.output('dataurlnewwindow');
};

// Builds the same work-summary PDF as generateServiceCallPDF/printServiceCallPDF
// but returns it as a base64 string + filename instead of saving/printing, so
// callers can attach it to an email (e.g. the Franklin completion send).
export const getServiceCallPDFBase64 = async (serviceCall, invoiceData = null) => {
  let data = resolveInvoiceData(serviceCall, invoiceData);
  if (serviceCall.isCspire) {
    data = await enrichInvoiceDataWithTax(serviceCall, data);
  }
  const { doc, serviceDate, clientName } = serviceCall.isCspire
    ? buildCspireWorkOrderPDF(serviceCall, data)
    : buildServiceCallPDF(serviceCall);

  const filename = `${(serviceDate || '').replace(/-/g, '')}_${clientName}_invoice.pdf`;
  const base64 = doc.output('datauristring').split(',')[1];
  return { base64, filename };
};

